import type { ProviderModel } from '~/modules/query/ai/types';

export interface ProviderModelOption {
  id: string;
  /** What the dropdown shows: `id — In $x / 1M, Out $y / 1M`. */
  label: string;
  pricingLabel: string;
}

export const PRICE_UNAVAILABLE_TEXT = 'Cost estimation unavailable';

function formatUsdPerMillion(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(value);
}

export function formatModelPricingLine(
  model: Pick<ProviderModel, 'inputCostPer1M' | 'outputCostPer1M'>,
): string {
  if (model.inputCostPer1M === null || model.outputCostPer1M === null) {
    return PRICE_UNAVAILABLE_TEXT;
  }

  return `In ${formatUsdPerMillion(model.inputCostPer1M)} / 1M, Out ${formatUsdPerMillion(model.outputCostPer1M)} / 1M`;
}

/**
 * Builds the model dropdown options from active provider models only. Inactive
 * models are never offered as a fallback; `isModelIdSelectable` lets a form
 * detect a saved record whose model has since gone inactive.
 */
export function buildActiveProviderModelOptions(
  models: ProviderModel[] | undefined,
): ProviderModelOption[] {
  return (models || [])
    .filter((model) => model.isActive)
    .map((model) => {
      const pricingLabel = formatModelPricingLine(model);

      return {
        id: model.id,
        label: `${model.id} — ${pricingLabel}`,
        pricingLabel,
      };
    });
}

export function isModelIdSelectable(
  options: ProviderModelOption[],
  modelId: string | undefined | null,
): boolean {
  const id = modelId?.trim();
  if (!id) {
    return false;
  }

  return options.some((option) => option.id === id);
}
